import { useState, useRef, useEffect } from "react";
import {
  Plug, CircleHelp,
  HardDrive, FileText, PenLine, BookOpen, Users2,
  CheckCircle, ExternalLink, Settings, ChevronRight, ChevronLeft,
  Inbox, AlertCircle, ChevronDown, Folder, FolderOpen,
  MoreVertical, Upload, MoveRight, Loader2,
  Wrench, Files, Zap, X,
  Grid3x3, Clock, Receipt, Shield, Search,
  Mail, Database, BarChart2, Bot, Brain,
  Building2, Layers, Wallet, Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { TOPICS } from "../custom/HelpSheet";
import { useLocation } from "wouter";
import { PdfToolModal, type PdfToolId } from "../custom/PdfToolModal";
import { ExcelToolModal, type ExcelToolId } from "../custom/ExcelToolModal";
import { CatchMeUpModal } from "../custom/CatchMeUpModal";
import { FixedAssetsCSModal } from "../custom/FixedAssetsCSModal";
import { useAuth } from "@/lib/authContext";
import {
  type AccountIntegration,
  type MakersHubStats,
  getAccountIntegration,
  saveAccountIntegration,
  deleteAccountIntegration,
  getMakersHubStats,
  getMakersHubForwardAddress,
  setMakersHubForwardAddress,
  fetchWithAuth,
} from "@/lib/api";

// ── Types ─────────────────────────────────────────────────────────────────────
type Tab = "integrations" | "tools" | "knowledge" | "help";
type IntegrationId = "smartvault" | "makershub" | "fixed-assets-cs" | "signwell" | "tbworks" | "buildertrend" | "procore" | "buildertrendwallet" | "proline" | "sosinventory";

const INTEGRATIONS: {
  id: IntegrationId;
  label: string;
  subtitle: string;
  icon: React.ElementType;
  iconBg: string;
  status: string;
  statusColor: string;
}[] = [
  // Display order: Ready (green) -> in progress (yellow) -> Coming Soon (grey).
  { id: "fixed-assets-cs",    label: "Fixed Assets CS",       subtitle: "TR Import Builder",          icon: Layers,     iconBg: "bg-blue-100 text-blue-700",     status: "Ready",               statusColor: "bg-green-100 text-green-800" },
  { id: "smartvault",         label: "SmartVault",            subtitle: "Being Replaced by R2",       icon: HardDrive,  iconBg: "bg-amber-100 text-amber-700",   status: "Planning Migration",  statusColor: "bg-yellow-100 text-yellow-800" },
  { id: "makershub",          label: "MakersHub",             subtitle: "AP Invoice Workflow",        icon: FileText,   iconBg: "bg-amber-100 text-amber-700",   status: "API in Progress",     statusColor: "bg-yellow-100 text-yellow-800" },
  { id: "buildertrend",       label: "Buildertrend",          subtitle: "Construction Management",    icon: Building2,  iconBg: "bg-orange-100 text-orange-700", status: "Coming Soon",         statusColor: "bg-slate-100 text-slate-700" },
  { id: "buildertrendwallet", label: "Buildertrend Wallet",   subtitle: "Construction Payments",      icon: Wallet,     iconBg: "bg-orange-100 text-orange-700", status: "Coming Soon",         statusColor: "bg-slate-100 text-slate-700" },
  { id: "procore",            label: "Procore",               subtitle: "Construction Project Mgmt",  icon: Layers,     iconBg: "bg-red-100 text-red-700",       status: "Coming Soon",         statusColor: "bg-slate-100 text-slate-700" },
  { id: "proline",            label: "Proline",               subtitle: "Project Management",         icon: BarChart2,  iconBg: "bg-blue-100 text-blue-700",     status: "Coming Soon",         statusColor: "bg-slate-100 text-slate-700" },
  { id: "sosinventory",       label: "SOS Inventory",         subtitle: "Inventory Management",       icon: Package,    iconBg: "bg-green-100 text-green-700",   status: "Coming Soon",         statusColor: "bg-slate-100 text-slate-700" },
  { id: "signwell",           label: "SignWell",              subtitle: "E-Signatures",               icon: PenLine,    iconBg: "bg-violet-100 text-violet-700", status: "Coming Soon",         statusColor: "bg-slate-100 text-slate-700" },
  { id: "tbworks",            label: "TB Works",              subtitle: "Trial Balance",              icon: BookOpen,   iconBg: "bg-slate-100 text-slate-700",   status: "Coming Soon",         statusColor: "bg-slate-100 text-slate-700" },
];

// ── Integration detail panels ─────────────────────────────────────────────────

function EmptyState({ icon, message, cta }: { icon: React.ReactNode; message: string; cta?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-8 px-4 text-center bg-muted/20">
      <div className="text-muted-foreground/50">{icon}</div>
      <p className="text-xs text-muted-foreground">{message}</p>
      {cta}
    </div>
  );
}

function ConnectButton({ label = "Connect" }: { label?: string }) {
  return (
    <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" disabled>
      <AlertCircle className="w-3.5 h-3.5 text-muted-foreground" />
      {label} — credentials required
    </Button>
  );
}

// ── SmartVault interactive folder tree ────────────────────────────────────────

type SVFile = { name: string; size: string; by: string; modified: string; type: "pdf" | "xlsx" | "docx" | "other" };
type SVFolder = { id: string; name: string; modified: string; created: string; children?: SVFolder[]; files?: SVFile[] };

const MOCK_TREE: SVFolder[] = [
  {
    id: "2025", name: "2025 Tax Returns", modified: "Apr 2, 2025", created: "Jan 10, 2025",
    children: [
      {
        id: "supporting", name: "Supporting Documents", modified: "Mar 14, 2025", created: "Jan 10, 2025",
        files: [
          { name: "W2_2025.pdf", size: "142 KB", by: "Ann Santos", modified: "Apr 2, 2025", type: "pdf" },
          { name: "1099_Interest_FirstNational.pdf", size: "38 KB", by: "Ann Santos", modified: "Mar 28, 2025", type: "pdf" },
          { name: "QuickBooks_Export_Q4.xlsx", size: "891 KB", by: "Cassie Carpenter", modified: "Feb 19, 2025", type: "xlsx" },
        ],
      },
      { id: "final", name: "Final Returns", modified: "Apr 14, 2025", created: "Jan 10, 2025" },
    ],
  },
  { id: "2024", name: "2024 Tax Returns", modified: "Nov 8, 2024", created: "Jan 5, 2024" },
  { id: "payroll", name: "Payroll", modified: "Apr 15, 2026", created: "Mar 1, 2023" },
  { id: "correspondence", name: "Correspondence", modified: "Feb 3, 2026", created: "Mar 1, 2023" },
  { id: "contracts", name: "Contracts", modified: "Jan 12, 2024", created: "Mar 1, 2023" },
];

const MOCK_STAGED = [
  { name: "Q1_Bank_Statement_2026.pdf", age: "2 days ago" },
  { name: "March_Invoice_from_client.pdf", age: "1 day ago" },
];

function fileIconColor(type: SVFile["type"]) {
  if (type === "pdf") return "text-red-400";
  if (type === "xlsx") return "text-emerald-500";
  if (type === "docx") return "text-blue-400";
  return "text-muted-foreground";
}

function FolderRow({
  folder, depth, expanded, selected,
  onToggle, onSelect,
}: {
  folder: SVFolder; depth: number;
  expanded: boolean; selected: boolean;
  onToggle: () => void; onSelect: () => void;
}) {
  const indent = depth * 16;
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 px-3 py-1.5 cursor-pointer hover:bg-slate-100 transition-colors text-xs select-none",
        selected ? "bg-blue-100 border-l-2 border-l-blue-500" : "border-l-2 border-l-transparent"
      )}
      style={{ paddingLeft: 12 + indent }}
      onClick={() => { onToggle(); onSelect(); }}
    >
      <ChevronRight
        className={cn("w-3 h-3 text-slate-500 shrink-0 transition-transform", expanded && "rotate-90")}
      />
      {expanded
        ? <FolderOpen className="w-3.5 h-3.5 text-amber-500 shrink-0" />
        : <Folder className="w-3.5 h-3.5 text-amber-500 shrink-0" />
      }
      <span className={cn("truncate flex-1 font-semibold text-slate-800", selected && "text-blue-700")}>{folder.name}</span>
      <span className="text-slate-500 shrink-0 hidden sm:block">{folder.modified}</span>
    </div>
  );
}

function FileRow({ file, depth }: { file: SVFile; depth: number }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div
      className="group flex items-center gap-1.5 px-3 py-1 hover:bg-slate-100 transition-colors text-xs"
      style={{ paddingLeft: 12 + depth * 16 }}
    >
      <span className="w-3 h-3 shrink-0" />
      <FileText className={cn("w-3.5 h-3.5 shrink-0", fileIconColor(file.type))} />
      <span className="truncate flex-1 text-slate-800 font-medium">{file.name}</span>
      <span className="text-slate-500 shrink-0 hidden sm:block">{file.size}</span>
      <div className="relative shrink-0">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-slate-200 transition-opacity"
        >
          <MoreVertical className="w-3 h-3 text-slate-500" />
        </button>
        {menuOpen && (
          <div
            className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-50 min-w-[120px]"
            onMouseLeave={() => setMenuOpen(false)}
          >
            <button className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100 flex items-center gap-2">
              <ExternalLink className="w-3 h-3" />Open
            </button>
            <button className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100 flex items-center gap-2">
              <MoveRight className="w-3 h-3" />Move To…
            </button>
            <button className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2">
              🗑 Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FolderTree({
  nodes, depth = 0, expanded, selected, onToggle, onSelect,
}: {
  nodes: SVFolder[]; depth?: number;
  expanded: Set<string>; selected: string | null;
  onToggle: (id: string) => void; onSelect: (id: string) => void;
}) {
  return (
    <>
      {nodes.map((folder) => (
        <div key={folder.id}>
          <FolderRow
            folder={folder}
            depth={depth}
            expanded={expanded.has(folder.id)}
            selected={selected === folder.id}
            onToggle={() => onToggle(folder.id)}
            onSelect={() => onSelect(folder.id)}
          />
          {expanded.has(folder.id) && (
            <>
              {folder.children && (
                <FolderTree
                  nodes={folder.children}
                  depth={depth + 1}
                  expanded={expanded}
                  selected={selected}
                  onToggle={onToggle}
                  onSelect={onSelect}
                />
              )}
              {folder.files?.map((f) => (
                <FileRow key={f.name} file={f} depth={depth + 1} />
              ))}
            </>
          )}
        </div>
      ))}
    </>
  );
}

function SmartVaultDetail() {
  const [, navigate] = useLocation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set(["2025", "supporting"]));
  const [selected, setSelected] = useState<string | null>("2025");
  const [stagedOpen, setStagedOpen] = useState(true);

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Credentials notice */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 shrink-0">
        <p className="text-[10px] text-amber-700 leading-tight">
          Preview only — live data loads once credentials are configured
        </p>
        <button
          onClick={() => navigate("/settings")}
          className="text-[10px] text-amber-700 underline hover:text-amber-900 shrink-0"
        >
          Settings
        </button>
      </div>

      {/* Upload toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-slate-50 shrink-0">
        <span className="text-xs text-slate-600 font-medium truncate">
          {selected ? `Selected: ${MOCK_TREE.find(f => f.id === selected)?.name ?? selected}` : "Select a folder to upload"}
        </span>
        <button
          disabled
          className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium bg-blue-500 text-white rounded opacity-50 cursor-not-allowed"
        >
          <Upload className="w-3 h-3" />Upload
        </button>
      </div>

      {/* Column headers */}
      <div className="grid text-[10px] font-semibold text-slate-600 uppercase tracking-wide px-3 py-1.5 border-b bg-slate-100 shrink-0" style={{ gridTemplateColumns: "1fr 60px 28px" }}>
        <span>Name</span>
        <span className="text-right pr-2">Size / Date</span>
        <span />
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto">
        <FolderTree
          nodes={MOCK_TREE}
          expanded={expanded}
          selected={selected}
          onToggle={toggle}
          onSelect={setSelected}
        />

        {/* Staged uploads */}
        <div className="border-t mt-1">
          <button
            onClick={() => setStagedOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-3 py-2 text-[10px] font-semibold text-slate-600 uppercase tracking-wide hover:bg-slate-100 bg-slate-50 border-b"
          >
            <ChevronDown className={cn("w-3 h-3 transition-transform", !stagedOpen && "-rotate-90")} />
            Staged Uploads
            <span className="ml-auto bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full text-[9px] font-medium normal-case tracking-normal">
              {MOCK_STAGED.length} pending
            </span>
          </button>
          {stagedOpen && (
            <div className="px-3 pb-3 space-y-1.5">
              {MOCK_STAGED.map((f) => (
                <div key={f.name} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded border border-amber-200 bg-amber-50">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <FileText className="w-3.5 h-3.5 text-red-400 shrink-0" />
                    <span className="text-xs text-slate-800 font-medium truncate">{f.name}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-slate-500">{f.age}</span>
                    <button
                      disabled
                      className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium border border-amber-400 text-amber-700 rounded hover:bg-amber-100 opacity-60 cursor-not-allowed"
                    >
                      Move to SV
                    </button>
                  </div>
                </div>
              ))}
              <p className="text-[10px] text-slate-500 pt-1">
                Select a folder above, then click "Move to SV" to push the file into SmartVault.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MakersHubDetail({ accountId }: { accountId: string | null }) {
  const { user } = useAuth();
  const canConnect = user?.role === "admin" || user?.role === "manager";
  const [integration, setIntegration] = useState<AccountIntegration | null | "loading">("loading");
  const [showInput, setShowInput] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [mhSubmitting, setMhSubmitting] = useState(false);
  const [mhLastId, setMhLastId] = useState<number | null>(null);
  const [mhError, setMhError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [stats, setStats] = useState<MakersHubStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [forwardEmail, setForwardEmail] = useState<string | null>(null);
  const [forwardInput, setForwardInput] = useState("");
  const [editingForward, setEditingForward] = useState(false);
  const [forwardSaving, setForwardSaving] = useState(false);
  const [forwardError, setForwardError] = useState<string | null>(null);
  // Most clients are forward-only (no API key), so the connect box asks for the
  // forward address by default; the key is available behind a toggle.
  const [connectMode, setConnectMode] = useState<"forward" | "key">("forward");

  async function handleFileSubmit(file: File) {
    if (!accountId) return;
    setMhSubmitting(true);
    setMhLastId(null);
    setMhError(null);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const result = await fetchWithAuth<{ invoiceId: number }>(
        `/api/accounts/${accountId}/makershub/submit-invoice`,
        { method: "POST", body: JSON.stringify({ data: base64, filename: file.name, type: file.type || "application/pdf" }) }
      );
      setMhLastId(result.invoiceId);
    } catch (err: any) {
      setMhError(err.message ?? "Submission failed");
    } finally {
      setMhSubmitting(false);
    }
  }

  useEffect(() => {
    if (!accountId) { setIntegration(null); return; }
    setIntegration("loading");
    getAccountIntegration(accountId, "makershub").then(result => {
      setIntegration(result);
      if (result) {
        loadStats(accountId);
        getMakersHubForwardAddress(accountId).then(r => setForwardEmail(r.forward_email)).catch(() => {});
      }
    });
  }, [accountId]);

  async function loadStats(aid: string) {
    setStatsLoading(true);
    try {
      const s = await getMakersHubStats(aid);
      setStats(s);
    } catch {
      setStats({ available: false });
    } finally {
      setStatsLoading(false);
    }
  }

  const descBox = (
    <div className="rounded-lg border bg-slate-50 px-3 py-2.5">
      <p className="text-xs text-slate-700 leading-relaxed">
        MakersHub streamlines your AP workflow. Connect to drag invoices from email directly into MakersHub for processing and approval.
      </p>
    </div>
  );

  if (!accountId) {
    return (
      <div className="flex flex-col gap-4">
        {descBox}
        <p className="text-xs text-muted-foreground text-center py-4">
          Open a client account to connect MakersHub.
        </p>
      </div>
    );
  }

  if (integration === "loading") {
    return <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">Loading…</div>;
  }

  async function handleSave() {
    if (!accountId || !apiKeyInput.trim()) return;
    setSaving(true);
    try {
      const saved = await saveAccountIntegration(accountId, "makershub", apiKeyInput.trim());
      setIntegration(saved);
      setShowInput(false);
      setApiKeyInput("");
    } finally {
      setSaving(false);
    }
  }

  async function handleDisconnect() {
    if (!accountId) return;
    setDisconnecting(true);
    try {
      await deleteAccountIntegration(accountId, "makershub");
      setIntegration(null);
    } finally {
      setDisconnecting(false);
    }
  }

  async function handleSaveForward() {
    if (!accountId) return;
    setForwardSaving(true);
    setForwardError(null);
    try {
      const r = await setMakersHubForwardAddress(accountId, forwardInput.trim());
      setForwardEmail(r.forward_email);
      setEditingForward(false);
    } catch (err: any) {
      setForwardError(err?.message ?? "Failed to save forward email");
    } finally {
      setForwardSaving(false);
    }
  }

  // Connect a forward-only client: the forward-address endpoint upserts the
  // integration row with an empty api_key, so no key is needed to get set up.
  async function handleConnectForward() {
    if (!accountId || !forwardInput.trim()) return;
    setForwardSaving(true);
    setForwardError(null);
    try {
      const r = await setMakersHubForwardAddress(accountId, forwardInput.trim());
      setForwardEmail(r.forward_email);
      setIntegration(await getAccountIntegration(accountId, "makershub"));
      setShowInput(false);
      setForwardInput("");
    } catch (err: any) {
      setForwardError(err?.message ?? "Failed to save forward email");
    } finally {
      setForwardSaving(false);
    }
  }

  if (integration) {
    return (
      <div className="flex flex-col gap-4">
        {descBox}
        <div className="flex items-center justify-between rounded-lg border bg-green-50 border-green-200 px-3 py-2">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-3.5 h-3.5 text-green-600" />
            <span className="text-xs font-medium text-green-800">Connected</span>
          </div>
          <span className="text-[10px] text-green-700">
            since {new Date(integration.created_at).toLocaleDateString()}
          </span>
        </div>

        {/* Invoice forward address (D1) — forward invoice emails straight to MakersHub */}
        <div className="flex flex-col gap-1.5">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Invoice Forward Email</p>
          {editingForward ? (
            <div className="flex flex-col gap-2">
              <input
                type="email"
                placeholder="invoices@client.makershub.ai"
                value={forwardInput}
                onChange={(e) => setForwardInput(e.target.value)}
                className="w-full rounded border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
              <div className="flex gap-2">
                <Button size="sm" className="h-7 text-xs flex-1" onClick={handleSaveForward} disabled={forwardSaving}>
                  {forwardSaving ? "Saving…" : "Save"}
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setEditingForward(false); setForwardError(null); }}>Cancel</Button>
              </div>
              {forwardError && <p className="text-[10px] text-destructive">{forwardError}</p>}
            </div>
          ) : forwardEmail ? (
            <div className="flex items-center justify-between gap-2 rounded border bg-muted/30 px-2.5 py-1.5">
              <span className="text-xs truncate" title={forwardEmail}>{forwardEmail}</span>
              {canConnect && (
                <button className="text-[10px] text-primary hover:underline shrink-0" onClick={() => { setForwardInput(forwardEmail); setEditingForward(true); }}>Edit</button>
              )}
            </div>
          ) : canConnect ? (
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setForwardInput(""); setEditingForward(true); }}>
              Set forward email
            </Button>
          ) : (
            <p className="text-[10px] text-muted-foreground italic">Not set — ask a manager to configure.</p>
          )}
        </div>

        {/* Stats + direct upload both push through the MakersHub API, so they only
            apply to clients that have a key. Forward-only clients skip them. */}
        {integration.has_api_key ? (
        <>
        {/* Invoice stats */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Invoice Stats</p>
            <button
              onClick={() => accountId && loadStats(accountId)}
              disabled={statsLoading}
              className="text-[10px] text-primary hover:underline disabled:opacity-50"
            >
              {statsLoading ? "Loading…" : "Refresh"}
            </button>
          </div>
          {statsLoading && !stats && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground py-2">
              <Loader2 className="w-3 h-3 animate-spin" />Fetching stats…
            </div>
          )}
          {stats && !stats.available && (
            <p className="text-[10px] text-muted-foreground italic">Stats unavailable — list API not supported by MakersHub.</p>
          )}
          {stats?.available && (
            <>
              <div className="grid grid-cols-2 gap-1.5">
                {([
                  { label: "Pending",  value: stats.pending,  color: "bg-amber-50 border-amber-200 text-amber-800" },
                  { label: "Overdue",  value: stats.overdue,  color: "bg-rose-50 border-rose-200 text-rose-800" },
                  { label: "Approved", value: stats.approved, color: "bg-green-50 border-green-200 text-green-800" },
                  { label: "Rejected", value: stats.rejected, color: "bg-slate-50 border-slate-200 text-slate-600" },
                ] as { label: string; value: number | undefined; color: string }[]).map(({ label, value, color }) => (
                  <div key={label} className={cn("rounded-lg border px-3 py-2 flex flex-col", color)}>
                    <span className="text-[10px] font-medium opacity-70">{label}</span>
                    <span className="text-lg font-bold leading-tight">{value ?? 0}</span>
                  </div>
                ))}
              </div>
              {(stats.pendingAmount ?? 0) > 0 && (
                <p className="text-[10px] text-muted-foreground text-right">
                  Pending amount: <span className="font-semibold text-amber-700">${stats.pendingAmount!.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </p>
              )}
              <p className="text-[10px] text-muted-foreground text-right">{stats.total} total invoices</p>
            </>
          )}
        </div>

        {/* Invoice drop / paste zone */}
        <div
          tabIndex={0}
          role="button"
          aria-label="Drop invoice or paste screenshot"
          className={cn(
            "flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-5 text-center cursor-pointer outline-none",
            "focus:ring-2 focus:ring-amber-400 focus:ring-offset-1 transition-colors",
            isDragOver ? "border-amber-400 bg-amber-50" : "border-slate-300 bg-slate-50 hover:border-amber-300 hover:bg-amber-50/40",
          )}
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file) handleFileSubmit(file);
          }}
          onPaste={(e) => {
            const item = Array.from(e.clipboardData.items).find(i => i.kind === "file");
            if (item) { const file = item.getAsFile(); if (file) handleFileSubmit(file); }
          }}
        >
          {mhSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
              <p className="text-xs text-amber-700">Submitting to MakersHub…</p>
            </>
          ) : mhLastId ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-500" />
              <p className="text-xs text-green-700 font-medium">Invoice #{mhLastId} created</p>
              <button className="text-[10px] text-muted-foreground hover:underline" onClick={() => setMhLastId(null)}>Submit another</button>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 text-slate-400" />
              <p className="text-xs text-slate-600 font-medium">Drop invoice or paste screenshot</p>
              <p className="text-[10px] text-muted-foreground">Click here, then Ctrl+V to paste an image</p>
              {mhError && <p className="text-[10px] text-destructive mt-1">{mhError}</p>}
            </>
          )}
        </div>
        </>
        ) : (
          <div className="flex flex-col gap-2 rounded-lg border bg-slate-50 px-3 py-2.5">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Forward-only — invoice emails go to MakersHub with the <span className="font-medium">→ MH</span> button on the inbox.
              Direct upload and invoice stats need an API key.
            </p>
            {canConnect && (showInput ? (
              <div className="flex flex-col gap-2">
                <input
                  type="password"
                  placeholder="Paste MakersHub API key"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  className="w-full rounded border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                />
                <div className="flex gap-2">
                  <Button size="sm" className="h-7 text-xs flex-1" onClick={handleSave} disabled={saving || !apiKeyInput.trim()}>
                    {saving ? "Saving…" : "Save key"}
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setShowInput(false); setApiKeyInput(""); }}>Cancel</Button>
                </div>
              </div>
            ) : (
              <button className="text-[10px] text-primary hover:underline self-start" onClick={() => setShowInput(true)}>
                Add an API key
              </button>
            ))}
          </div>
        )}
        <a href="https://makershub.ai/app/#/finance/bills" target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs w-full">
            <ExternalLink className="w-3.5 h-3.5" />Open MakersHub
          </Button>
        </a>
        {user?.role === "admin" && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 w-full"
            onClick={handleDisconnect}
            disabled={disconnecting}
          >
            {disconnecting ? "Disconnecting…" : "Disconnect"}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {descBox}
      {showInput ? (
        connectMode === "forward" ? (
          <div className="flex flex-col gap-2">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Invoice Forward Email</p>
            <input
              type="email"
              placeholder="invoices@client.makershub.ai"
              value={forwardInput}
              onChange={(e) => setForwardInput(e.target.value)}
              className="w-full rounded border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                className="h-7 text-xs flex-1"
                onClick={handleConnectForward}
                disabled={forwardSaving || !forwardInput.trim()}
              >
                {forwardSaving ? "Saving…" : "Save"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => { setShowInput(false); setForwardInput(""); setForwardError(null); }}
              >
                Cancel
              </Button>
            </div>
            {forwardError && <p className="text-[10px] text-destructive">{forwardError}</p>}
            <button
              className="text-[10px] text-muted-foreground hover:underline self-start"
              onClick={() => { setConnectMode("key"); setForwardError(null); }}
            >
              Use an API key instead
            </button>
          </div>
        ) : (
        <div className="flex flex-col gap-2">
          <input
            type="password"
            placeholder="Paste MakersHub API key"
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
            className="w-full rounded border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-7 text-xs flex-1"
              onClick={handleSave}
              disabled={saving || !apiKeyInput.trim()}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => { setShowInput(false); setApiKeyInput(""); }}
            >
              Cancel
            </Button>
          </div>
          <button
            className="text-[10px] text-muted-foreground hover:underline self-start"
            onClick={() => setConnectMode("forward")}
          >
            Use a forward email instead
          </button>
        </div>
        )
      ) : canConnect ? (
        <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs w-full" onClick={() => setShowInput(true)}>
          <Plug className="w-3.5 h-3.5" />Connect MakersHub
        </Button>
      ) : (
        <p className="text-xs text-muted-foreground text-center py-2">
          Ask a manager to connect MakersHub for this account.
        </p>
      )}
    </div>
  );
}

function SignWellDetail() {
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border bg-violet-50 border-violet-200 px-3 py-2.5">
        <p className="text-xs text-violet-800 leading-relaxed font-medium">Replacing DocuSign via SmartVault</p>
        <p className="text-xs text-violet-700 leading-relaxed mt-1">
          SignWell is SOC 2 Type 2 certified and legally equivalent to DocuSign at ~$10/mo. Once connected, e-signature requests can be triggered from client profiles and signed PDFs stored automatically.
        </p>
      </div>
      <EmptyState
        icon={<PenLine className="w-8 h-8" />}
        message="No pending envelopes — connect SignWell to get started"
        cta={<ConnectButton label="Connect SignWell" />}
      />
      <a href="https://www.signwell.com" target="_blank" rel="noopener noreferrer">
        <Button variant="ghost" size="sm" className="gap-1.5 h-8 text-xs w-full text-violet-700">
          <ExternalLink className="w-3.5 h-3.5" />Open SignWell
        </Button>
      </a>
    </div>
  );
}

function TBWorksDetail() {
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border bg-slate-50 px-3 py-2.5">
        <p className="text-xs text-slate-700 leading-relaxed">
          TB Works is your local trial balance application (Microsoft Access backend). Phase 2 will add deep-link access and file export from client projects.
        </p>
      </div>
      <EmptyState
        icon={<BookOpen className="w-8 h-8" />}
        message="Deep-link and export coming in Phase 2"
        cta={
          <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" disabled>
            Launch TB Works — local app
          </Button>
        }
      />
    </div>
  );
}

function FixedAssetsCSDetail() {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-4 px-1">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
          <Layers className="w-5 h-5 text-blue-700" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Fixed Assets CS</p>
          <p className="text-xs text-muted-foreground">Thomson Reuters Import Builder</p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Convert a client's raw fixed asset schedule (CSV or Excel) into a clean, validated
        workbook ready for import into Thomson Reuters Fixed Assets CS. Automatically maps
        common column names and flags validation errors before import.
      </p>
      <div className="rounded-md border bg-muted/30 p-3 space-y-1.5 text-xs text-muted-foreground">
        <p className="font-semibold text-foreground text-[11px] uppercase tracking-wide mb-1.5">What it does</p>
        <div className="flex items-start gap-2"><span className="text-blue-500 shrink-0">→</span><span>Accepts CSV, XLSX, or XLS asset schedules</span></div>
        <div className="flex items-start gap-2"><span className="text-blue-500 shrink-0">→</span><span>Auto-detects and maps column headers to FA CS fields</span></div>
        <div className="flex items-start gap-2"><span className="text-blue-500 shrink-0">→</span><span>Validates descriptions, dates, asset numbers, costs, methods</span></div>
        <div className="flex items-start gap-2"><span className="text-blue-500 shrink-0">→</span><span>Outputs a 5-sheet workbook with import-ready and error tabs</span></div>
      </div>
      <Button className="w-full gap-2" onClick={() => setOpen(true)}>
        <Layers className="w-4 h-4" />
        Open Fixed Assets CS Builder
      </Button>
      {open && <FixedAssetsCSModal onClose={() => setOpen(false)} />}
    </div>
  );
}

function IntegrationDetail({ id, accountId }: { id: IntegrationId; accountId: string | null }) {
  switch (id) {
    case "smartvault":      return <SmartVaultDetail />;
    case "makershub":       return <MakersHubDetail accountId={accountId} />;
    case "fixed-assets-cs": return <FixedAssetsCSDetail />;
    case "signwell":        return <SignWellDetail />;
    case "tbworks":         return <TBWorksDetail />;
  }
}

// ── Help pane ─────────────────────────────────────────────────────────────────

const HELP_SECTIONS: {
  id: string;
  label: string;
  icon: React.ElementType;
  topicIds: string[];
}[] = [
  {
    id: "time-billing",
    label: "Time & Billing",
    icon: Clock,
    topicIds: [
      "logging-time",
      "timesheet",
      "submitting",
      "approvals",
      "pto",
      "pay-periods",
      "status-meanings",
      "invoicing",
    ],
  },
  {
    id: "clients-projects",
    label: "Clients & Projects",
    icon: FolderOpen,
    topicIds: [
      "clients",
      "client-groups",
      "projects",
      "tasks",
      "workflow",
      "tags",
      "notifications",
      "financial-sources",
      "documents",
    ],
  },
  {
    id: "communication",
    label: "Communication",
    icon: Mail,
    topicIds: [
      "emails",
      "messages",
      "sms",
      "calendar",
    ],
  },
  {
    id: "admin-settings",
    label: "Admin & Settings",
    icon: Shield,
    topicIds: [
      "admin-config",
      "reports",
      "daily-report",
    ],
  },
];

function HelpPane() {
  const [activeId, setActiveId] = useState(TOPICS[0].id);
  const [query, setQuery] = useState("");
  const [openSections, setOpenSections] = useState<Set<string>>(
    new Set(HELP_SECTIONS.map((s) => s.id))
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const [navWidth, setNavWidth] = useState(212);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const isResizing = useRef(false);

  function startResize(e: React.MouseEvent) {
    e.preventDefault();
    isResizing.current = true;
    const startX = e.clientX;
    const startW = navWidth;
    const onMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      // handle sits on the right edge
      setNavWidth(Math.max(120, Math.min(360, startW + (ev.clientX - startX))));
    };
    const onUp = () => {
      isResizing.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  const filtered = query.trim()
    ? TOPICS.filter((t) => t.label.toLowerCase().includes(query.toLowerCase()))
    : null;
  const active = TOPICS.find((t) => t.id === activeId) ?? TOPICS[0];

  function selectTopic(id: string) {
    setActiveId(id);
    setQuery("");
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }

  function toggleSection(id: string) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // Build a lookup map for quick topic access
  const topicMap = Object.fromEntries(TOPICS.map((t) => [t.id, t]));

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Topic list */}
      {navCollapsed ? (
        <div className="w-9 shrink-0 border-r bg-muted/30 flex flex-col items-center pt-2">
          <button
            onClick={() => setNavCollapsed(false)}
            title="Show topics"
            aria-label="Show topics"
            aria-expanded={false}
            className="p-1.5 rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
      <>
      <nav
        style={{ width: navWidth }}
        className="shrink-0 border-r bg-muted/30 flex flex-col overflow-hidden"
      >
        <div className="p-2 pb-1 shrink-0 flex items-center gap-1">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full pl-7 pr-2 py-1.5 text-[13px] rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <button
            onClick={() => setNavCollapsed(true)}
            title="Hide topics"
            aria-label="Hide topics"
            aria-expanded={true}
            className="shrink-0 p-1 rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex flex-col gap-0.5 p-2 pt-1 overflow-y-auto flex-1">
          {filtered ? (
            /* Search results — flat list */
            <>
              {filtered.length === 0 && (
                <p className="text-[13px] text-muted-foreground px-2 py-3 text-center">No results</p>
              )}
              {filtered.map((topic) => (
                <button
                  key={topic.id}
                  onClick={() => selectTopic(topic.id)}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-[13px] transition-colors w-full ${
                    activeId === topic.id
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  <span className="shrink-0">{topic.icon}</span>
                  <span className="leading-tight truncate">{topic.label}</span>
                </button>
              ))}
            </>
          ) : (
            /* Sectioned accordion */
            <>
              {HELP_SECTIONS.map((section) => {
                const SectionIcon = section.icon;
                const isOpen = openSections.has(section.id);
                const sectionTopics = section.topicIds
                  .map((id) => topicMap[id])
                  .filter(Boolean);
                return (
                  <div key={section.id}>
                    {/* Section header */}
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="flex items-center gap-1.5 w-full px-1 py-1.5 text-left rounded hover:bg-muted/40 transition-colors"
                    >
                      <SectionIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="flex-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground truncate">
                        {section.label}
                      </span>
                      {isOpen
                        ? <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
                        : <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                      }
                    </button>
                    {/* Topics */}
                    {isOpen && sectionTopics.map((topic) => (
                      <button
                        key={topic.id}
                        onClick={() => selectTopic(topic.id)}
                        className={`flex items-center gap-2 pl-4 pr-2 py-1.5 rounded-md text-left text-[13px] transition-colors w-full ${
                          activeId === topic.id
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        }`}
                      >
                        <span className="shrink-0">{topic.icon}</span>
                        <span className="leading-tight truncate">{topic.label}</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </nav>

      {/* resize handle */}
      <div
        onMouseDown={startResize}
        title="Drag to resize"
        className="w-1.5 shrink-0 cursor-col-resize hover:bg-primary/40 transition-colors"
      />
      </>
      )}

      {/* Content */}
      <div ref={contentRef} className="flex-1 overflow-y-auto px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          {active.icon}
          {active.label}
        </h2>
        <div className="text-[13px]">{active.content}</div>
      </div>
    </div>
  );
}

// ── Integrations pane ─────────────────────────────────────────────────────────
function IntegrationsPane() {
  const [selected, setSelected] = useState<IntegrationId | null>(null);
  const active = selected ? INTEGRATIONS.find((i) => i.id === selected)! : null;
  const [location] = useLocation();
  const accountMatch = location.match(/^\/accounts\/([^/]+)/);
  const accountId = accountMatch ? accountMatch[1] : null;

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {selected && active ? (
        <>
          {/* Detail header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b shrink-0 bg-muted/20">
            <button
              onClick={() => setSelected(null)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              All
            </button>
            <div className={cn("flex h-8 w-8 items-center justify-center rounded-lg shrink-0", active.iconBg)}>
              {(() => { const Icon = active.icon; return <Icon className="w-4 h-4" />; })()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-tight truncate">{active.label}</p>
              <p className="text-xs text-muted-foreground truncate">{active.subtitle}</p>
            </div>
            <span className={cn("text-xs px-2 py-0.5 rounded font-medium shrink-0", active.statusColor)}>
              {active.status}
            </span>
          </div>

          {/* Detail content */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <IntegrationDetail id={selected} accountId={accountId} />
          </div>
        </>
      ) : (
        /* List view */
        <div className="flex-1 overflow-y-auto py-1">
          {INTEGRATIONS.map((intg) => {
            const Icon = intg.icon;
            return (
              <button
                key={intg.id}
                onClick={() => setSelected(intg.id)}
                className="w-full flex items-center gap-3.5 px-4 py-3.5 border-b border-border/60 last:border-b-0 hover:bg-muted/50 transition-colors text-left group"
              >
                <div className={cn("flex h-10 w-10 items-center justify-center rounded-lg shrink-0", intg.iconBg)}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{intg.label}</p>
                  <p className="text-[13px] text-muted-foreground truncate">{intg.subtitle}</p>
                </div>
                <span className={cn("text-xs px-2 py-0.5 rounded font-medium shrink-0 whitespace-nowrap", intg.statusColor)}>
                  {intg.status}
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Tools pane ────────────────────────────────────────────────────────────────

function ComingSoonBadge() {
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-500 border border-slate-200">
      Coming Soon
    </span>
  );
}

function AmberBanner({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
      <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
      <p className="text-[11px] text-amber-800 leading-snug">{children}</p>
    </div>
  );
}

interface ToolItem {
  emoji: string;
  name: string;
  url?: string;
  action?: PdfToolId;
  excelAction?: ExcelToolId;
}
interface ToolSubcategory {
  label: string;
  items: ToolItem[];
}
interface ToolCategory {
  id: string;
  label: string;
  icon: React.ElementType;
  iconColor: string;
  subcategories: ToolSubcategory[];
  subTabs?: { id: string; label: string; subcategories: ToolSubcategory[] }[];
}

const TOOL_CATEGORIES: ToolCategory[] = [
  {
    id: "qb",
    label: "QuickBooks Imports",
    icon: Database,
    iconColor: "text-green-600",
    subcategories: [],
    subTabs: [
      {
        id: "ar",
        label: "A/R",
        subcategories: [
          {
            label: "A/R & Revenue",
            items: [
              { emoji: "🧾", name: "Invoice (A/R) Importer" },
              { emoji: "🧾", name: "Sales Receipt Importer" },
              { emoji: "📝", name: "Estimate Importer" },
              { emoji: "📄", name: "Credit Memo Importer" },
              { emoji: "💳", name: "Customer Payment Importer" },
            ],
          },
        ],
      },
      {
        id: "ap",
        label: "A/P",
        subcategories: [
          {
            label: "A/P & Expenses",
            items: [
              { emoji: "📋", name: "Bill (A/P) Importer" },
              { emoji: "🔄", name: "Vendor Credits Importer" },
              { emoji: "📦", name: "Purchase Order Importer" },
              { emoji: "✏️", name: "Checks Importer" },
              { emoji: "💳", name: "Credit Card Charge Importer" },
              { emoji: "💳", name: "Credit Card Credit Importer" },
              { emoji: "💸", name: "Bill Payment Importer" },
            ],
          },
        ],
      },
      {
        id: "banking",
        label: "Banking",
        subcategories: [
          {
            label: "Banking & Transfers",
            items: [
              { emoji: "🏦", name: "Bank Statement Importer" },
              { emoji: "📊", name: "Credit Card Statement Importer" },
              { emoji: "💰", name: "Deposit Importer" },
              { emoji: "🔄", name: "Transfer Importer" },
            ],
          },
        ],
      },
      {
        id: "payroll",
        label: "Payroll",
        subcategories: [
          {
            label: "Payroll & HR",
            items: [
              { emoji: "⏱️", name: "SG Development Payroll Import" },
              { emoji: "📦", name: "Purchase Order Automation" },
              { emoji: "⏱️", name: "B2 Payroll Import" },
            ],
          },
        ],
      },
      {
        id: "ref",
        label: "Ref Data",
        subcategories: [
          {
            label: "Reference Data",
            items: [
              { emoji: "👤", name: "Customer Importer" },
              { emoji: "🏪", name: "Vendor Importer" },
              { emoji: "👥", name: "Employee Importer" },
              { emoji: "📦", name: "Item Importer" },
              { emoji: "📊", name: "Chart of Accounts Importer" },
              { emoji: "📓", name: "Journal Entry Importer" },
              { emoji: "⏰", name: "Time Tracking Importer" },
            ],
          },
        ],
      },
    ],
  },
  {
    id: "docs",
    label: "Document Tools",
    icon: Files,
    iconColor: "text-orange-500",
    subcategories: [],
    subTabs: [
      {
        id: "pdf",
        label: "PDF",
        subcategories: [
          {
            label: "Basic Operations",
            items: [
              { emoji: "📊", name: "Financial Statements Builder" },
              { emoji: "🖼️", name: "Convert Images to PDF", action: "from-images" },
              { emoji: "📑", name: "Merge PDFs", action: "merge" },
              { emoji: "✂️", name: "Extract Pages", action: "extract-pages" },
              { emoji: "🔄", name: "Rotate Pages", action: "rotate" },
            ],
          },
          {
            label: "Document Cleanup",
            items: [
              { emoji: "🗜️", name: "Compress PDF", action: "optimize" },
              { emoji: "🧹", name: "Clean Scanned Documents (Deskew)" },
              { emoji: "🔧", name: "Repair Corrupted PDFs", action: "repair" },
            ],
          },
          {
            label: "Splitting & Organization",
            items: [
              { emoji: "📂", name: "Split Bulk PDFs (AI)", action: "smart-split" },
            ],
          },
          {
            label: "Text & Data",
            items: [
              { emoji: "🔍", name: "Extract Data from Scans (Vision + CSV)", action: "vision-extract" },
              { emoji: "🔎", name: "OCR Text Extraction", action: "ocr" },
              { emoji: "📝", name: "Extract Text from PDF", action: "text" },
            ],
          },
          {
            label: "Bookmarks",
            items: [
              { emoji: "🔖", name: "Add Bookmarks", action: "bookmarks-add" },
              { emoji: "📋", name: "List / View Bookmarks", action: "bookmarks-list" },
              { emoji: "📥", name: "Import Bookmarks from CSV", action: "bookmarks-import" },
              { emoji: "📤", name: "Export Bookmarks to CSV", action: "bookmarks-export" },
              { emoji: "✏️", name: "Edit / Remove Bookmarks", action: "bookmarks-remove" },
            ],
          },
          {
            label: "Metadata & Security",
            items: [
              { emoji: "ℹ️", name: "Get PDF Info / Details", action: "info" },
              { emoji: "🖊️", name: "Edit PDF Metadata", action: "metadata" },
              { emoji: "🔒", name: "Add Password to PDF", action: "password-add" },
              { emoji: "🔓", name: "Remove Password from PDF", action: "password-remove" },
            ],
          },
          {
            label: "Conversions",
            items: [
              { emoji: "🔄", name: "Convert DOCX / Markdown / HTML → PDF" },
              { emoji: "🖼️", name: "PDF to Images" },
            ],
          },
          {
            label: "Advanced",
            items: [
              { emoji: "⚡", name: "Optimize PDF for Web", action: "optimize" },
            ],
          },
        ],
      },
      {
        id: "excel",
        label: "Excel",
        subcategories: [
          {
            label: "Basic Conversion",
            items: [
              { emoji: "🔄", name: "Convert Excel to CSV", excelAction: "to-csv" },
              { emoji: "🔄", name: "Convert CSV to Excel", excelAction: "to-xlsx" },
            ],
          },
          {
            label: "Format Conversion",
            items: [
              { emoji: "🔀", name: "Convert .xls ↔ .xlsx", excelAction: "to-xlsx" },
              { emoji: "📊", name: "Convert to Macro-Enabled (.xlsm)" },
              { emoji: "📋", name: "Convert to Template (.xltx/.xltm)" },
              { emoji: "💾", name: "Convert to Binary (.xlsb)" },
            ],
          },
          {
            label: "Organization",
            items: [
              { emoji: "🔗", name: "Merge Multiple Excel Files", excelAction: "merge" },
              { emoji: "✂️", name: "Split Excel by Sheet", excelAction: "split" },
            ],
          },
          {
            label: "Data Cleanup",
            items: [
              { emoji: "🧹", name: "Clean Data (Remove Duplicates / Blanks)" },
              { emoji: "📊", name: "Analyze Data (Summary Statistics)" },
            ],
          },
          {
            label: "Formatting",
            items: [
              { emoji: "✨", name: "Format Spreadsheet (Headers, Widths, Styles)" },
              { emoji: "📌", name: "Extract Specific Columns / Rows" },
            ],
          },
        ],
      },
      {
        id: "word",
        label: "Word",
        subcategories: [
          {
            label: "Conversion",
            items: [
              { emoji: "📝", name: "Convert Word to PDF" },
              { emoji: "📄", name: "Convert PDF to Word" },
            ],
          },
          {
            label: "Coming Soon",
            items: [
              { emoji: "📝", name: "5 additional Word operations coming soon" },
            ],
          },
        ],
      },
      {
        id: "image",
        label: "Image",
        subcategories: [
          {
            label: "Basic Operations",
            items: [
              { emoji: "🖼️", name: "Convert Format (JPG/PNG/HEIC/WebP/GIF)" },
              { emoji: "📐", name: "Resize Images (Dimensions or %)" },
              { emoji: "✂️", name: "Crop Images" },
              { emoji: "🔄", name: "Rotate Images (90°/180°/270°)" },
            ],
          },
          {
            label: "Image Enhancement",
            items: [
              { emoji: "🗜️", name: "Compress / Optimize" },
              { emoji: "✨", name: "Enhance Quality (Sharpen, Contrast, Brightness)" },
              { emoji: "🧹", name: "Remove Background" },
              { emoji: "📸", name: "Fix Orientation (Auto-rotate from EXIF)" },
            ],
          },
          {
            label: "Batch Processing",
            items: [
              { emoji: "🔄", name: "Batch Convert (Multiple Images)" },
              { emoji: "📐", name: "Batch Resize (Thumbnail Generation)" },
              { emoji: "🖼️", name: "Add Watermark (Text or Logo)" },
              { emoji: "🧩", name: "Create Collage / Grid" },
            ],
          },
        ],
      },
    ],
  },
  {
    id: "tb",
    label: "Trial Balance & Tax",
    icon: BarChart2,
    iconColor: "text-blue-600",
    subcategories: [],
    subTabs: [
      {
        id: "trial",
        label: "Trial Bal",
        subcategories: [
          {
            label: "Trial Balance",
            items: [
              { emoji: "📅", name: "Multi-Period QXL → Trial Balance (Monthly)" },
              { emoji: "📅", name: "Multi-Period QXL → Trial Balance (Weekly)" },
            ],
          },
        ],
      },
      {
        id: "pipeline",
        label: "TB Works",
        subcategories: [
          {
            label: "TB Works Pipeline",
            items: [
              { emoji: "➡️", name: "QXL → TB Works Import", url: "https://n8n-docker-af1e.onrender.com/form/6e04f25b-5546-4117-9cca-847626e0bff5" },
              { emoji: "➡️", name: "TB Works → UltraTax Import", url: "https://n8n-docker-af1e.onrender.com/form/13e9fe3c-3606-46c8-a2c7-3ab6f7c68627" },
            ],
          },
        ],
      },
      {
        id: "analysis",
        label: "Job & Billing",
        subcategories: [
          {
            label: "Job & Billing Analysis",
            items: [
              { emoji: "📊", name: "Extract QB Job Data (PY & CY)" },
              { emoji: "🔧", name: "G2 Billing Info Normalizer" },
            ],
          },
        ],
      },
    ],
  },
  {
    id: "ai",
    label: "Chatbots & AI",
    icon: Bot,
    iconColor: "text-purple-500",
    subcategories: [
      {
        label: "Tax Research",
        items: [
          { emoji: "🔍", name: "UltraTax 1040 Lookup", url: "https://n8n-docker-af1e.onrender.com/webhook/f91bd649-8b3e-4b46-85f7-f88c7f87653b/chat" },
        ],
      },
      {
        label: "HR",
        items: [
          { emoji: "👥", name: "Employee Evaluations" },
        ],
      },
    ],
  },
  {
    id: "auto",
    label: "Automations",
    icon: Zap,
    iconColor: "text-amber-500",
    subcategories: [
      {
        label: "Construction",
        items: [
          { emoji: "🏗️", name: "AIA Billing & Retainage Extraction" },
        ],
      },
      {
        label: "Accounting",
        items: [
          { emoji: "📝", name: "Notes Payable Workflow" },
        ],
      },
      {
        label: "SmartSuite",
        items: [
          { emoji: "📋", name: "SmartSuite Template Fillers" },
        ],
      },
    ],
  },
  {
    id: "writing",
    label: "Email & Writing",
    icon: Mail,
    iconColor: "text-violet-500",
    subcategories: [],
    subTabs: [
      {
        id: "writing",
        label: "Writing",
        subcategories: [
          {
            label: "Writing",
            items: [
              { emoji: "✍️", name: "Draft Email from Scratch" },
              { emoji: "💬", name: "Write Reply" },
              { emoji: "📝", name: "Expand Brief Text" },
            ],
          },
        ],
      },
      {
        id: "editing",
        label: "Editing",
        subcategories: [
          {
            label: "Editing",
            items: [
              { emoji: "🔄", name: "Reword / Rephrase" },
              { emoji: "📋", name: "Summarize Long Text" },
              { emoji: "✅", name: "Grammar & Spelling Check" },
            ],
          },
        ],
      },
      {
        id: "tone",
        label: "Tone",
        subcategories: [
          {
            label: "Tone & Style",
            items: [
              { emoji: "💼", name: "Make Professional" },
              { emoji: "😊", name: "Make Friendly" },
              { emoji: "⚠️", name: "Check Tone (Misinterpretation Risk)" },
              { emoji: "📋", name: "Use Template (Common Scenarios)" },
              { emoji: "🌐", name: "Translate" },
            ],
          },
        ],
      },
    ],
  },
];

type FlatTool = ToolItem & { categoryLabel: string; subcategoryLabel: string };

function getAllTools(): FlatTool[] {
  const result: FlatTool[] = [];
  for (const cat of TOOL_CATEGORIES) {
    const subs = cat.subTabs
      ? cat.subTabs.flatMap(t => t.subcategories)
      : (cat.subcategories ?? []);
    for (const sub of subs) {
      for (const item of sub.items) {
        result.push({ ...item, categoryLabel: cat.label, subcategoryLabel: sub.label });
      }
    }
  }
  return result;
}

function ToolsPane() {
  const [location] = useLocation();
  const accountMatch = location.match(/^\/accounts\/([^/]+)/);
  const onClientPage = !!accountMatch;
  const accountId = accountMatch ? accountMatch[1] : null;
  const [catchMeUpOpen, setCatchMeUpOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<Record<string, string>>({
    docs: "pdf",
    qb: "ar",
    writing: "writing",
    tb: "trial",
  });
  const [activePdfTool, setActivePdfTool] = useState<{ id: PdfToolId; name: string } | null>(null);
  const [activeExcelTool, setActiveExcelTool] = useState<{ id: ExcelToolId; name: string } | null>(null);
  const [toolSearch, setToolSearch] = useState("");
  const [view, setView] = useState<"firm" | "client">("firm");

  function toggleCategory(id: string) {
    setOpenCategory((prev) => (prev === id ? null : id));
  }

  function renderToolRow(item: ToolItem, key: string) {
    const clickable = !!(item.url || item.action || item.excelAction);
    return (
      <div
        key={key}
        className={cn(
          "flex items-center gap-2 py-1 px-2 rounded transition-colors",
          clickable ? "hover:bg-muted/50 cursor-pointer" : "hover:bg-muted/30"
        )}
        onClick={
          item.url ? () => window.open(item.url, "_blank") :
          item.action ? () => setActivePdfTool({ id: item.action!, name: item.name }) :
          item.excelAction ? () => setActiveExcelTool({ id: item.excelAction!, name: item.name }) :
          undefined
        }
      >
        <span className="text-sm shrink-0 leading-none">{item.emoji}</span>
        <span className="text-[13px] text-foreground truncate flex-1">{item.name}</span>
        {item.url ? (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 border border-green-200 shrink-0">
            <ExternalLink className="w-2.5 h-2.5" />
            Launch
          </span>
        ) : (item.action || item.excelAction) ? (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 border border-blue-200 shrink-0">
            Open
          </span>
        ) : (
          <ComingSoonBadge />
        )}
      </div>
    );
  }

  function renderSubcategories(subcategories: ToolSubcategory[]) {
    return subcategories.map((sub) => (
      <div key={sub.label}>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mt-2.5 mb-1">
          {sub.label}
        </p>
        {sub.items.map((item) => renderToolRow(item, item.name))}
      </div>
    ));
  }

  const q = toolSearch.toLowerCase().trim();
  const searchResults: FlatTool[] = q
    ? getAllTools().filter(t => t.name.toLowerCase().includes(q))
    : [];

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* toggle */}
      <div className="flex items-center gap-1 px-4 pt-4 pb-3 shrink-0">
        <div className="flex rounded-md border border-border overflow-hidden w-full">
          <button
            onClick={() => setView("firm")}
            className={cn(
              "flex-1 py-1.5 text-[13px] font-medium transition-colors",
              view === "firm"
                ? "bg-primary text-primary-foreground"
                : "bg-card text-muted-foreground hover:bg-muted/40"
            )}
          >
            Firm
          </button>
          <button
            onClick={() => setView("client")}
            className={cn(
              "flex-1 py-1.5 text-[13px] font-medium transition-colors",
              view === "client"
                ? "bg-primary text-primary-foreground"
                : "bg-card text-muted-foreground hover:bg-muted/40"
            )}
          >
            Client
          </button>
        </div>
      </div>

      {view === "firm" ? (
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={toolSearch}
          onChange={e => setToolSearch(e.target.value)}
          placeholder="Search tools…"
          className="w-full pl-8 pr-8 py-1.5 text-[13px] rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-ring"
        />
        {toolSearch && (
          <button
            onClick={() => setToolSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Search results */}
      {q ? (
        searchResults.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-6">No tools match "{toolSearch}"</p>
        ) : (
          <div className="space-y-0.5">
            {searchResults.map((item, i) => (
              <div key={i}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mt-2.5 mb-0.5">
                  {item.categoryLabel} · {item.subcategoryLabel}
                </p>
                {renderToolRow(item, `${item.name}-${i}`)}
              </div>
            ))}
          </div>
        )
      ) : (
      <>
      {/* Category accordions */}
      {TOOL_CATEGORIES.map((cat) => {
        const CatIcon = cat.icon;
        const isOpen = openCategory === cat.id;
        const toolCount = cat.subTabs
          ? cat.subTabs.flatMap(t => t.subcategories.flatMap(s => s.items)).length
          : cat.subcategories.flatMap(s => s.items).length;
        return (
          <div key={cat.id} className="rounded-lg border border-border overflow-hidden">
            {/* Category header */}
            <button
              onClick={() => toggleCategory(cat.id)}
              className="flex items-center gap-2 w-full py-2 px-2 rounded-lg hover:bg-muted/40 transition-colors text-left"
            >
              <CatIcon className={cn("w-4 h-4 shrink-0", cat.iconColor)} />
              <span className="flex-1 text-[13px] font-semibold text-foreground truncate">{cat.label}</span>
              <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full shrink-0">
                {toolCount}
              </span>
              {isOpen
                ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              }
            </button>

            {/* Subcategories and tools */}
            {isOpen && (
              <div className="px-2 pb-2 space-y-0.5">
                {cat.subTabs ? (
                  <>
                    {/* Sub-tab switcher */}
                    <div className="flex gap-1 mb-3 flex-wrap pt-1">
                      {cat.subTabs.map((tab) => (
                        <button
                          key={tab.id}
                          onClick={(e) => { e.stopPropagation(); setActiveSubTab(prev => ({ ...prev, [cat.id]: tab.id })); }}
                          className={cn(
                            "text-[10px] font-semibold px-2.5 py-1 rounded-full transition-colors",
                            (activeSubTab[cat.id] ?? cat.subTabs![0].id) === tab.id
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:bg-muted/60 border border-border"
                          )}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                    {/* Active tab's subcategories */}
                    {(() => {
                      const activeTab = cat.subTabs.find(t => t.id === (activeSubTab[cat.id] ?? cat.subTabs![0].id)) ?? cat.subTabs[0];
                      return renderSubcategories(activeTab.subcategories);
                    })()}
                  </>
                ) : (
                  renderSubcategories(cat.subcategories)
                )}
              </div>
            )}
          </div>
        );
      })}

      </>
      )}
      </div>
      ) : (
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
        <p className="text-sm font-semibold text-foreground">Client Tools</p>

        {onClientPage && accountId ? (
          <div className="space-y-2">
            {/* AI Briefing */}
            <button
              onClick={() => setCatchMeUpOpen(true)}
              className="w-full flex items-center gap-3 rounded-lg border border-violet-200 bg-violet-50 hover:bg-violet-100 px-3 py-2.5 text-left transition-colors group"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-100 group-hover:bg-violet-200 transition-colors shrink-0">
                <Brain className="w-3.5 h-3.5 text-violet-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-violet-900 truncate">Catch me up</p>
                <p className="text-[10px] text-violet-600 truncate">AI briefing on this client</p>
              </div>
              <span className="text-[10px] font-medium text-violet-600 shrink-0">→</span>
            </button>

            {/* Future client tools — placeholder */}
            {[
              { icon: Zap,     name: "Payroll Import Workflow", description: "n8n automation for payroll processing" },
              { icon: Grid3x3, name: "SmartSuite Portal",       description: "Client-specific SmartSuite application" },
            ].map(({ icon: Icon, name, description }) => (
              <div
                key={name}
                className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 opacity-60"
              >
                <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{description}</p>
                </div>
                <ComingSoonBadge />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">
            Navigate to a client profile to see client-specific tools.
          </p>
        )}
      </div>
      )}

      {catchMeUpOpen && accountId && (
        <CatchMeUpModal
          accountId={accountId}
          onClose={() => setCatchMeUpOpen(false)}
        />
      )}
      {activePdfTool && (
        <PdfToolModal
          toolId={activePdfTool.id}
          toolName={activePdfTool.name}
          onClose={() => setActivePdfTool(null)}
        />
      )}
      {activeExcelTool && (
        <ExcelToolModal
          toolId={activeExcelTool.id}
          toolName={activeExcelTool.name}
          onClose={() => setActiveExcelTool(null)}
        />
      )}
    </div>
  );
}

// ── Knowledge pane ────────────────────────────────────────────────────────────

const FIRM_CATEGORIES: {
  icon: React.ElementType;
  label: string;
  count: number;
}[] = [
  { icon: Clock,      label: "Time Tracking & Timesheets", count: 3 },
  { icon: FolderOpen, label: "Projects & Workflows",       count: 5 },
  { icon: Receipt,    label: "Invoicing & Billing",        count: 2 },
  { icon: Users2,     label: "Client Management",          count: 4 },
  { icon: Shield,     label: "Admin & Settings",           count: 3 },
];

const CLIENT_ARTICLES = [
  { title: "Payroll Processing Procedures" },
  { title: "Monthly Bookkeeping Checklist" },
  { title: "Year-End Tax Document Requirements" },
];

function KnowledgePane() {
  const [location] = useLocation();
  const accountMatch = location.match(/^\/accounts\/([^/]+)/);
  const onClientPage = !!accountMatch;
  const [view, setView] = useState<"firm" | "client">("firm");

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Toggle */}
      <div className="flex items-center gap-1 px-4 pt-4 pb-3 shrink-0">
        <div className="flex rounded-md border border-border overflow-hidden w-full">
          <button
            onClick={() => setView("firm")}
            className={cn(
              "flex-1 py-1.5 text-[13px] font-medium transition-colors",
              view === "firm"
                ? "bg-primary text-primary-foreground"
                : "bg-card text-muted-foreground hover:bg-muted/40"
            )}
          >
            Firm
          </button>
          <button
            onClick={() => setView("client")}
            className={cn(
              "flex-1 py-1.5 text-[13px] font-medium transition-colors",
              view === "client"
                ? "bg-primary text-primary-foreground"
                : "bg-card text-muted-foreground hover:bg-muted/40"
            )}
          >
            Client
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
        {view === "firm" ? (
          <>
            <p className="text-sm font-semibold text-foreground">Firm Knowledge Base</p>

            <div className="rounded-lg border border-border overflow-hidden divide-y divide-border">
              {FIRM_CATEGORIES.map(({ icon: Icon, label, count }) => (
                <button
                  key={label}
                  disabled
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/30 transition-colors cursor-not-allowed"
                >
                  <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 text-[13px] text-foreground truncate">{label}</span>
                  <span className="text-[11px] text-muted-foreground shrink-0 mr-1">{count} articles</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>

            <AmberBanner>
              Article creation and management coming soon. Use the Help tab for current documentation.
            </AmberBanner>

            <p className="text-[10px] text-muted-foreground text-center">
              Powered by Central Command Knowledge Base
            </p>
          </>
        ) : (
          <>
            {onClientPage ? (
              <>
                <p className="text-sm font-semibold text-foreground">Client-Specific Knowledge</p>

                <div className="rounded-lg border border-border overflow-hidden divide-y divide-border">
                  {CLIENT_ARTICLES.map(({ title }) => (
                    <div key={title} className="flex items-start gap-3 px-3 py-2.5">
                      <FileText className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-foreground">{title}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">Last updated: —</p>
                      </div>
                    </div>
                  ))}
                </div>

                <AmberBanner>
                  Client-specific knowledge articles coming soon.
                </AmberBanner>
              </>
            ) : (
              <p className="text-[13px] text-muted-foreground italic">
                Navigate to a client profile to see client-specific knowledge articles.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main sidebar ──────────────────────────────────────────────────────────────
interface RightSidebarProps {
  open: boolean;
  defaultTab?: Tab;
  onOpenChange: (open: boolean) => void;
}

export function RightSidebar({ open, defaultTab = "integrations", onOpenChange }: RightSidebarProps) {
  const [activeTab, setActiveTab] = useState<Tab>(defaultTab);

  // Keep tab in sync when parent opens to a specific tab
  const prevDefaultTab = useRef(defaultTab);
  if (defaultTab !== prevDefaultTab.current) {
    prevDefaultTab.current = defaultTab;
    setActiveTab(defaultTab);
  }

  // escape closes the panel but lets nested dialogs consume the first key
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-in fade-in duration-150"
      onClick={() => onOpenChange(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Integrations and Help"
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col w-full max-w-3xl lg:max-w-4xl xl:max-w-5xl h-[85vh] xl:h-[90vh] bg-card text-foreground border border-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
      >
        {/* Tab bar + close */}
        <div className="flex items-stretch border-b shrink-0">
          {(
            [
              { id: "integrations", label: "Integrations", icon: Plug },
              { id: "tools",        label: "Tools",         icon: Wrench },
              { id: "knowledge",    label: "Knowledge",     icon: BookOpen },
              { id: "help",         label: "Help",          icon: CircleHelp },
            ] as { id: Tab; label: string; icon: React.ElementType }[]
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={cn(
                "flex-1 py-2.5 text-[10px] font-medium flex flex-col items-center justify-center gap-1 transition-colors",
                activeTab === id
                  ? "border-b-2 border-primary text-primary bg-primary/5"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 shrink-0 border-l border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            title="Close panel"
            aria-label="Close panel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* content */}
        <div className="flex flex-1 overflow-hidden">
          {activeTab === "integrations" && <IntegrationsPane />}
          {activeTab === "tools"        && <ToolsPane />}
          {activeTab === "knowledge"    && <KnowledgePane />}
          {activeTab === "help"         && <HelpPane />}
        </div>
      </div>
    </div>
  );
}
